import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { MapPinIcon } from '@heroicons/react/24/outline';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type { IncidentLocation } from '@/services/analytics.service';

interface IncidentLocationChartProps {
  data: IncidentLocation[];
}

const IncidentLocationChart: React.FC<IncidentLocationChartProps> = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Top Incident Locations</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center h-[300px] text-center">
            <MapPinIcon className="w-12 h-12 text-secondary-300 dark:text-secondary-600 mb-3" />
            <p className="text-sm font-medium text-secondary-500 dark:text-secondary-400">
              No incident location data available
            </p>
            <p className="text-xs text-secondary-400 dark:text-secondary-500 mt-1">
              Location data will appear once incidents with locations are recorded
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const chartHeight = Math.max(300, data.length * 40);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Top Incident Locations</CardTitle>
            <p className="text-sm text-secondary-600 dark:text-secondary-400 mt-1">
              Most frequent incident locations by count
            </p>
          </div>
          <span className="text-xs text-secondary-500 dark:text-secondary-400 bg-secondary-100 dark:bg-secondary-700 px-2 py-1 rounded-full">
            {data.length} locations
          </span>
        </div>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={chartHeight}>
          <BarChart
            data={data}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" strokeOpacity={0.5} horizontal={false} />
            <XAxis
              type="number"
              tick={{ fontSize: 12, fill: '#64748b' }}
              tickLine={{ stroke: '#cbd5e1' }}
              axisLine={{ stroke: '#cbd5e1' }}
            />
            <YAxis
              type="category"
              dataKey="location"
              width={180}
              tick={{ fontSize: 12, fill: '#64748b' }}
              tickLine={{ stroke: '#cbd5e1' }}
              axisLine={{ stroke: '#cbd5e1' }}
            />
            <Tooltip
              content={({ active, payload }) => {
                if (active && payload?.length) {
                  const item = payload[0].payload as IncidentLocation;
                  return (
                    <div className="bg-white dark:bg-secondary-800 p-4 border border-primary-200 dark:border-primary-700/50 rounded-lg shadow-xl">
                      <p className="text-sm font-semibold text-secondary-900 dark:text-white mb-1">
                        {item.location}
                      </p>
                      <p className="text-sm text-secondary-700 dark:text-secondary-300">
                        {item.count} incident{item.count !== 1 ? 's' : ''}
                      </p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Bar
              dataKey="count"
              fill="#00AEC7"
              radius={[0, 8, 8, 0]}
              animationDuration={300}
              animationEasing="ease-out"
            />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

export default IncidentLocationChart;
